import { useEffect, useRef } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { RealtimeChannel } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

// A running job broadcasts after every few genies, so updates are grouped into one refetch.
const REFRESH_DELAY_MS = 3000;

export const useBulkJobRealtime = () => {
  const queryClient = useQueryClient();
  const channelRef = useRef<RealtimeChannel | null>(null);

  useEffect(() => {
    if (channelRef.current) return;

    let refreshTimer: ReturnType<typeof setTimeout> | null = null;

    const refresh = () => {
      queryClient.invalidateQueries({ queryKey: ['bulk-update-jobs'] });
      queryClient.invalidateQueries({ queryKey: ['bulk-update-job-items'] });
    };

    const scheduleRefresh = () => {
      if (refreshTimer) return;
      refreshTimer = setTimeout(() => {
        refreshTimer = null;
        refresh();
      }, REFRESH_DELAY_MS);
    };

    channelRef.current = supabase
      .channel('agent-updates')
      .on('broadcast', { event: 'bulk_update_processed' }, scheduleRefresh)
      .on('broadcast', { event: 'bulk_job_completed' }, scheduleRefresh)
      .subscribe((status) => {
        // Also fires on reconnect, which picks up anything broadcast while disconnected.
        if (status === 'SUBSCRIBED') refresh();
      });

    return () => {
      if (refreshTimer) clearTimeout(refreshTimer);
      channelRef.current?.unsubscribe();
      channelRef.current = null;
    };
  }, [queryClient]);
};
